import { DetailsEditor, Loader } from "uikit"
import api from "api"
import AccountsConnector from "components/accounts/AccountsConnector"
import React, { useState, useCallback } from "react"
import { Link } from "react-router-dom"
import { Button, Grid, Icon } from "semantic-ui-react"
import { useStore } from "store"
import { ConnectionLinks } from "./ConnectionLinks"
import "./ManageLinkedAccounts.css"

export const ManageLinkedAccounts = ({ household, actions }) => {
  const householdId = household.id
  const [isPending, setIsPending] = useState(false)
  const [showAccountsConnector, setShowAccountsConnector] = useState(false)
  const [loaderText, setLoaderText] = useState(null)
  const [linkId, setLinkId] = useState(null)

  const {
    deleteConnectionLink,
    ignoreConnectionAccount,
    unIgnoreConnectionAccount,
    loadHouseholdConnections,
    refreshHouseholdConnections,
    loadHouseholdConnectionLinks,
    updateHouseholdConnectionsRefreshStatus
  } = actions
  const [householdConnections] = useStore("householdConnections", householdId)
  const connections = Object.values(householdConnections)
  const [connectionLinksObject] = useStore(
    "householdConnectionLinks",
    householdId
  )
  const connectionLinks = Object.values(connectionLinksObject)
  const clearPending = () => {
    setIsPending(false)
    setLoaderText(null)
  }

  const refreshLink = useCallback(link_id => {
    setLinkId(`${link_id}&refresh=True`)
    setShowAccountsConnector(true)
  }, [])

  const updateCredentials = useCallback(link_id => {
    setLinkId(link_id)
    setShowAccountsConnector(true)
  }, [])

  const deleteLink = useCallback(
    async provider_id => {
      setLoaderText(
        `Unlinking ${
          connectionLinks.find(({ id }) => id === provider_id).name
        }...`
      )
      setIsPending(true)
      try {
        await deleteConnectionLink({
          household_id: householdId,
          provider_id
        })
        clearPending()
      } catch (error) {
        clearPending()
      }
    },
    [connectionLinks, householdId, deleteConnectionLink]
  )

  const ignoreConnection = useCallback(
    async connection_id => {
      setLoaderText(
        `Ignoring ${connections.find(({ id }) => id === connection_id).name}...`
      )
      setIsPending(true)
      try {
        await ignoreConnectionAccount({
          household_id: householdId,
          connection_id
        })
        clearPending()
      } catch (error) {
        clearPending()
      }
    },
    [connections, householdId, ignoreConnectionAccount]
  )

  const unignoreConnection = useCallback(
    async account_id => {
      let unignoredAccount = connections.find(({ id }) => id === account_id)
      setLoaderText(`Reactivating ${unignoredAccount.name}...`)
      setIsPending(true)
      try {
        await unIgnoreConnectionAccount({
          household_id: householdId,
          id: account_id
        })
        clearPending()
      } catch (error) {
        clearPending()
      }
    },
    [connections, householdId, unIgnoreConnectionAccount]
  )

  const refreshAllProviders = useCallback(async () => {
    setIsPending(true)
    try {
      await refreshHouseholdConnections(householdId)
      clearPending()
    } catch (error) {
      clearPending()
    }
  }, [householdId, refreshHouseholdConnections])

  return (
    <React.Fragment>
      <Loader active={isPending} text={loaderText} />
      {showAccountsConnector ? (
        <AccountsConnector
          getFastLink={messageId =>
            `${api.baseURL}households/${householdId}/connections/linker?message_id=${messageId}&link_id=${linkId}&refresh`
          }
          onSuccess={async () => {
            await updateHouseholdConnectionsRefreshStatus(householdId)
            await loadHouseholdConnections(householdId)
            await loadHouseholdConnectionLinks(householdId)
            setShowAccountsConnector(false)
          }}
          onFailure={() => {
            setShowAccountsConnector(false)
          }}
        />
      ) : null}
      <DetailsEditor
        className={"white"}
        iconImage={() => <Icon size="huge" name="university" />}
        heading={"Manage Linked Accounts"}
        description={() => (
          <div>
            <p>
              See all the accounts you've linked, including ones we don't
              support or accounts you've ignored by deleting.
            </p>
            {connectionLinks.length > 0 ? (
              <Button
                primary
                basic
                content="Refresh All"
                onClick={refreshAllProviders}
              />
            ) : null}
          </div>
        )}
      >
        <Grid className="white" textAlign="left">
          {connectionLinks.length === 0 ? (
            <Grid.Row style={{ minHeight: "23em" }}>
              <Grid.Column textAlign="center" verticalAlign="middle">
                <p
                  style={{
                    fontSize: "1.8em",
                    fontWeight: "300"
                  }}
                >
                  No linked accounts.
                </p>
              </Grid.Column>
            </Grid.Row>
          ) : (
            connectionLinks.map(provider => (
              <ConnectionLinks
                {...provider}
                accounts={connections}
                key={provider.id}
                refreshLink={refreshLink}
                deleteLink={deleteLink}
                ignoreConnection={ignoreConnection}
                unignoreConnection={unignoreConnection}
                updateCredentials={updateCredentials}
              />
            ))
          )}
          <Grid.Row style={{ borderTop: "1px solid #ccc" }}>
            <Grid.Column textAlign="right" style={{ paddingRight: "2em" }}>
              <Link to="/organize/accounts+property">
                <Button content="Done" primary type="button" />
              </Link>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </DetailsEditor>
    </React.Fragment>
  )
}
